import type { ReactNode } from 'react';

import type { PortProps } from '../Port/Port';
import type { HardwareSlotsLayout } from '../Slots/SlotsRendered';
import { styled } from '../../stitches.config';
import { Device } from '../Device/Device';
import { HardwareSlotsLayoutRenderer } from '../Slots/SlotsRendered';
import { HardwareDeviceModel } from '../utils';
import {
  SwitchLayoutsMS10,
  SwitchLayoutsMS11,
  SwitchLayoutsMS12,
  SwitchLayoutsS1,
  SwitchLayoutsS2,
  SwitchLayoutsS3,
} from './SwitchLayouts/SwitchLayouts';

export type SwitchModel =
  | HardwareDeviceModel.Ms10
  | HardwareDeviceModel.Ms11
  | HardwareDeviceModel.Ms12
  | HardwareDeviceModel.S1
  | HardwareDeviceModel.S2
  | HardwareDeviceModel.S3;

// Type guard to check if a DeviceModel is a valid SwitchModel
export function isSwitchModel(deviceModel: string): deviceModel is SwitchModel {
  return [
    HardwareDeviceModel.Ms10,
    HardwareDeviceModel.Ms11,
    HardwareDeviceModel.Ms12,
    HardwareDeviceModel.S1,
    HardwareDeviceModel.S2,
    HardwareDeviceModel.S3,
  ].includes(deviceModel as SwitchModel);
}

// Safe conversion function from DeviceModel to SwitchModel
export function toSwitchModel(deviceModel: string): SwitchModel | null {
  return isSwitchModel(deviceModel) ? deviceModel : null;
}

type SwitchProps = {
  label?: ReactNode;
  metadata?: ReactNode;
  model: SwitchModel;
  ports: PortProps[];
};

function getSwitchLayout(model: SwitchProps['model']): HardwareSlotsLayout {
  switch (model) {
    case HardwareDeviceModel.Ms10:
      return SwitchLayoutsMS10;
    case HardwareDeviceModel.Ms11:
      return SwitchLayoutsMS11;
    case HardwareDeviceModel.Ms12:
      return SwitchLayoutsMS12;
    case HardwareDeviceModel.S1:
      return SwitchLayoutsS1;
    case HardwareDeviceModel.S2:
      return SwitchLayoutsS2;
    case HardwareDeviceModel.S3:
      return SwitchLayoutsS3;
    default:
      return SwitchLayoutsMS10;
  }
}

export function getSwitchPortOrientation(
  model: SwitchModel,
  portNumber: number,
): 'up' | 'down' | undefined {
  const layout = getSwitchLayout(model);

  for (const column of layout.columns) {
    for (const row of column.rows) {
      if (row.ports.includes(portNumber)) {
        return row.portOrientation;
      }
    }
  }

  return undefined;
}

const SwitchMetadata = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
});
SwitchMetadata.displayName = 'SwitchMetadata';

export function Switch({ label, metadata, model, ports }: SwitchProps) {
  return (
    <Device
      metadata={metadata && <SwitchMetadata>{metadata}</SwitchMetadata>}
      slots={
        <HardwareSlotsLayoutRenderer
          model={model}
          label={{ content: label ?? '', as: undefined, to: undefined }}
          layout={getSwitchLayout(model)}
          ports={ports}
        />
      }
    />
  );
}
